import { useState } from "react";
import { Checkbox } from "antd";
import { Icon } from "@iconify/react";
import { AppModal } from "@/shared/components/dialog";
import { Button } from "@/shared/components/button";

export function RecordsFilterModal({
  options,
  selected,
  onApply,
  onClose,
}: {
  /** Every resource type currently present in the active tab's records, as (type, label) pairs. */
  options: { type: string; label: string }[];
  /** Currently-applied resource types, or null when no filter is applied (everything shown). */
  selected: string[] | null;
  onApply: (types: string[]) => void;
  onClose: () => void;
}) {
  // Starts from the currently-applied filter, or ALL options checked when
  // nothing is filtered yet — selected === null means "no filter applied,
  // everything is shown," so the checkboxes should visually reflect that
  // (all checked, "Clear all" to narrow down) rather than showing an empty,
  // "Select all" state while the modal is actually displaying every resource.
  const [draft, setDraft] = useState<Set<string>>(new Set(selected ?? options.map((o) => o.type)));
  const allSelected = draft.size === options.length && options.length > 0;

  const toggle = (type: string) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  return (
    <AppModal
      open
      onOpenChange={(open) => { if (!open) onClose(); }}
      width={720}
      hideCloseButton
      className="[&_.ant-modal-content]:!rounded-3xl [&_.ant-modal-content]:!bg-[#F3F4F6] [&_.ant-modal-content]:!p-6"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-extrabold text-[#0A2947]">Filters</h2>
          <p className="mt-1 text-sm text-[#9CA3AF]">Select the data to explore more</p>
        </div>
        <Button
          variant="ghost"
          onClick={onClose}
          aria-label="Close"
          className="!flex !h-8 !w-8 !shrink-0 !items-center !justify-center !rounded-full !p-0 !text-[#0A2947] hover:!bg-[#E5E7EB] hover:!text-[#0A2947]"
        >
          <Icon icon="lucide:x" className="h-5 w-5" />
        </Button>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-x-6 gap-y-3">
        {options.map(({ type, label }) => (
          <Checkbox
            key={type}
            checked={draft.has(type)}
            onChange={() => toggle(type)}
            className="msq-filter-checkbox [&_.ant-checkbox]:!rounded-none"
          >
            <span className="whitespace-nowrap text-sm text-[#3A3D42]">{label}</span>
          </Checkbox>
        ))}
      </div>

      <div className="mt-6 flex items-center justify-between gap-4">
        <Button
          variant="link"
          onClick={() => setDraft(allSelected ? new Set() : new Set(options.map((o) => o.type)))}
          className="!h-auto !p-0 !text-sm !font-semibold !text-[#6B7280]"
        >
          {allSelected ? "Clear all" : "Select all"}
        </Button>
        <Button
          onClick={() => onApply([...draft])}
          className="!h-9 !rounded-full !bg-[#F17893] !px-5 !text-sm !font-bold !text-white hover:!bg-[#E85D7C]"
        >
          Apply
        </Button>
      </div>
    </AppModal>
  );
}
